import * as React from 'react';
import { Script, type HeadFC, type PageProps } from 'gatsby';
import { Layout } from '../components/templates/layout/Layout';
import { Section } from '../components/atom/section/Section';
import { Wrapper } from '../components/atom/wrapper/Wrapper';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Button } from '../components/atom/button/Button';
import { ProductTiles } from '../components/organisms/productTiles/ProductTiles';
import { Trustpilot } from '../components/organisms/trustpilot/Trustpilot';
import { SEO } from '../components/seo';
import { useHomepage } from '../hooks/index/useHomepage';
import { Paragraph } from '../components/atom/paragraph/Paragraph';
import { FaqsSection } from '../components/organisms/faqsSection/FaqsSection';
import { Heading } from '../components/atom/heading/Heading';
import '../components/pages/index/style.css';

const IndexPage: React.FC<PageProps> = () => {
	const { data, faqs, helped_clients } = useHomepage();

	return (
		<Layout>
			<Section className={'bg-ep-navy !pb-8 min-h-[75vh]'}>
				<Wrapper className="flex flex-col lg:flex-row">
					<div className="flex flex-col items-start shrink-0 lg:mr-12 pt-20">
						<h1 className={'text-headline text-white font-light mb-24'}>
							Better business,
							<br />
							on your terms.
						</h1>
						<div className="flex flex-col max-w-[600px]">
							<Paragraph
								className="text-subhead !font-light"
								color="text-white"
							>
								"Invoice finance allowed us to grow a lot faster. Without it, we
								may not even be here."
							</Paragraph>
							<Paragraph color="text-white" className="font-normal mb-28">
								Paul Buccini, Director, Buccini Transport
							</Paragraph>
						</div>
						<Button path={'/sign-up'} className="mb-20 lg:mb-0">
							Talk to us
						</Button>
					</div>
					<div className="flex items-end">
						<figure className="w-full h-[320px] rounded-tl-[90px] overflow-hidden">
							<GatsbyImage
								image={data.heroImage.gatsbyImageData}
								alt=""
								objectFit="cover"
							/>
						</figure>
					</div>
				</Wrapper>
			</Section>

			<Section className={'bg-ep-steel items-start'}>
				<Wrapper className="flex flex-col">
					<Heading className="mb-12" level="h2">
						{data.financeOptionsHeader}
					</Heading>
					<Wrapper>
						<ProductTiles />
					</Wrapper>
				</Wrapper>
			</Section>
			<Section className="bg-ep-blue !py-10">
				<Wrapper>
					<Trustpilot type="horizontal" theme="dark" />
				</Wrapper>
			</Section>
			<Section>
				<Wrapper>
					<Heading level="h2" className="mb-16">
						Clients we've recently helped.
					</Heading>

					<ul className="grid md:grid-cols-2 small-desktop:grid-cols-3 gap-12">
						{helped_clients.map((client) => (
							<li
								key={client.industry}
								className="bg-ep-steel p-8 rounded-tr-[80px] flex flex-col"
							>
								{!!client.icon?.url ? (
									<figure className="mb-12 pointer-events-none self-center">
										<img src={client.icon.url} />
									</figure>
								) : null}
								<Paragraph>
									<strong>Industry:</strong> {client.industry}
								</Paragraph>
								<Paragraph>
									<strong>Trading since:</strong> {client.tradingSince}
								</Paragraph>
								<Paragraph>
									<strong>Solution:</strong> {client.solution}
								</Paragraph>
								{client.facilityLimit ? (
									<Paragraph>
										<strong>Facility limit:</strong> {client.facilityLimit}
									</Paragraph>
								) : null}
								{client.loanAmount ? (
									<Paragraph>
										<strong>Loan amount:</strong> {client.loanAmount}
									</Paragraph>
								) : null}
								<Paragraph className="mb-0">
									<strong>Purpose:</strong> {client.purpose}
								</Paragraph>
							</li>
						))}
					</ul>
				</Wrapper>
			</Section>

			<Section className={'flex-col bg-ep-navy'}>
				<Wrapper>
					<Wrapper maxWidth={1080}>
						<Heading
							level="h2"
							color="text-ep-white"
							className="!text-headline font-light"
						>
							Starting, maintaining or growing a business.
							<div className="text-ep-blue mt-3">
								You need payments and people you can rely on.
							</div>
						</Heading>
					</Wrapper>
				</Wrapper>
			</Section>
			{/* <Section className={'min-h-[800px] uncentered'}>
					<Wrapper>
						<Wrapper size="small">
							<Heading level="h2" className="!text-white mb-2">
								{data.forBusinessHeader}
							</Heading>
							<Paragraph
								className="text-white mb-6 p__innerHtml--white"
								innerHtml
							>
								{stripParagraphTags(data.forBusinessDescription)}
							</Paragraph>
							<Button path="/sign-up">{data.forBusinessCta}</Button>
						</Wrapper>
						<Wrapper>
							<BusinessTypes />
						</Wrapper>
					</Wrapper>
				</Section> */}
			<FaqsSection faqs={faqs} theme="dark" sectionClass="bg-ep-navy" />
		</Layout>
	);
};

export default IndexPage;

export const Head: HeadFC = () => {
	const {
		data: { metaTags, seoKeywords },
	} = useHomepage();

	return (
		<>
			<SEO title={'Home'} metaTags={metaTags} keywords={seoKeywords} />
			<Script
				type="text/javascript"
				src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
				async
			/>
		</>
	);
};
